import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { AUTH_ME_QUERY_KEY, useAppUser } from '../lib/auth/use-app-user.js';

/**
 * Admin Dashboard visibility is gated exclusively on `data.roles` from
 * GET /api/v1/auth/me (backend-confirmed, Neon-sourced) -- never on
 * Clerk's user object, email address, or any frontend constant. This is
 * still only a UX nicety: the /admin route itself is independently guarded
 * (RequireAdmin) and the Worker re-checks authorization on every API call
 * regardless of what this menu shows.
 */
export function UserMenu() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { data: appUser } = useAppUser();
  // Matches the Worker's actual admin-dashboard gate (`requireAnyRole(['ADMIN', 'STAFF'])`,
  // see rbac.md) -- a STAFF account sees this link too, not just ADMIN.
  const canAccessAdmin = appUser?.roles.some((role) => role === 'ADMIN' || role === 'STAFF') ?? false;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleSignOut() {
    setOpen(false);
    // Clerk's own sign-out call -- never a homegrown session-clearing
    // routine. Clears the local cached user immediately so no stale
    // admin-only UI can render for even one frame while the redirect
    // happens, then navigates away from any page that may have required
    // auth to reach.
    await signOut();
    queryClient.removeQueries({ queryKey: AUTH_ME_QUERY_KEY });
    navigate(ROUTE_NAMES.home, { replace: true });
  }

  const displayName = user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? 'Account';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="hover:border-brand flex items-center gap-2 rounded-full border border-gray-200 py-1 pl-1 pr-3 text-sm font-medium text-gray-900"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {user?.imageUrl ? (
          <img src={user.imageUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <span className="bg-brand text-brand-foreground flex h-7 w-7 items-center justify-center rounded-full">
            {displayName.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="max-w-[8rem] truncate">{displayName}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-2xl border border-gray-100 bg-white py-1 shadow-xl"
        >
          <Link
            to={ROUTE_NAMES.accountProfile}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Profile
          </Link>
          <Link
            to={ROUTE_NAMES.accountOrders}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            My Orders
          </Link>
          <Link
            to={ROUTE_NAMES.accountWishlist}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Wishlist
          </Link>
          <Link
            to={ROUTE_NAMES.accountAddresses}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Addresses
          </Link>
          <Link
            to={ROUTE_NAMES.accountRewards}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Rewards
          </Link>
          <Link
            to={ROUTE_NAMES.accountNotifications}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Notifications
          </Link>
          {canAccessAdmin && (
            <Link
              to={ROUTE_NAMES.adminDashboard}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Admin Dashboard
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className={cn(
              'block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50',
            )}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
